import React, { FC } from 'react';

import { SelectedItemsFromLocalStorage } from '@/components/contextMenu/mwdContextMenu/types';
import { useMultiVersterkenDialog } from '@/components/dialogs/versterken/useMultiVersterkenDialog';
import MwdDialog from '@/components/UI/Dialog/Dialog';
import { CreatableSelectField } from '@/components/UI/Fields/ReactFormFields/CreatableSelectField';
import { RadioField } from '@/components/UI/Fields/ReactFormFields/RadioField';
import { Label } from '@/components/UI/Label/Label';
import Notification from '@/components/UI/Notification/Notification';
import { formatTime } from '@/utils/dateUtils';

import styles from '@/components/UI/Dialog/Dialog.module.css';

type MultiVersterkenDataProps = {
  showDialog: boolean;
  handleOpenChange: (p: boolean) => void;
  details: SelectedItemsFromLocalStorage[];
};

const MultiVersterkenDialog: FC<MultiVersterkenDataProps> = ({ showDialog, handleOpenChange, details }) => {
  const {
    onSubmit,
    control,
    handleSubmit,
    resetDialog,
    handleEenheidNummerChange,
    detailsSortedOnTime,
    inzetPosities,
    teVersterkenEenheidNummers,
    hasErrors,
    errorMessage,
  } = useMultiVersterkenDialog(details, handleOpenChange, showDialog);

  return (
    <MwdDialog
      title='Versterken'
      className={styles.multiMutatieDialogStyle}
      showDialog={showDialog}
      handleOpenChange={handleOpenChange}
      data-testid='contextMenu-multi-versterken'
      hasCloseInHeader={true}
      showNotification={!!errorMessage}
      notificationType='error'
      notificationText={errorMessage}
    >
      <Notification style={{ height: '48px', marginBottom: '12px' }} iconAlignment='center'>
        Let op: je gaat meerdere inzetten versterken
      </Notification>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ marginTop: '12px', marginBottom: '12px' }}>
          <Label>Treinnummers</Label>
          <div
            style={{
              color: 'var(--tractie-color-content-default)',
              marginBottom: '0px',
              display: 'flex',
              columnGap: '40px',
              flexWrap: 'wrap',
            }}
          >
            {details.map((d) => (
              <div
                key={`${d.treinnummer}${d.inzetPositie ? `-${d.inzetPositie}` : ''}`}
              >{`${d.treinnummer}${d.inzetPositie ? `-${d.inzetPositie}` : ''}`}</div>
            ))}
          </div>
        </div>
        <div style={{ marginTop: '12px', marginBottom: '12px', maxWidth: '60%' }}>
          <CreatableSelectField
            placeholder='Selecteer eenheid...'
            createLabel='Zoek eenheid:'
            onChangeCallback={(e) => handleEenheidNummerChange(e as string)}
            control={control}
            testId='eenheid-select'
            required
            matchFromStart
            name='eenheid'
            label='Eenheid'
            options={teVersterkenEenheidNummers}
          />
        </div>
        <div
          style={{
            backgroundColor: 'var(--tractie-color-border-subtle)',
            height: '2px',
            width: '100%',
            marginBottom: '14px',
            marginTop: '14px',
          }}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            columnGap: '130px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Van (eerste inzet)</Label>
            <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '12px' }}>
              {`${detailsSortedOnTime[0].dienstregelpuntVan} - ${formatTime(new Date(detailsSortedOnTime[0].vertrekTijd))}`}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Label>Tot (laatste inzet)</Label>
            <div style={{ color: 'var(--tractie-color-content-default)', marginBottom: '12px' }}>
              {`${detailsSortedOnTime[detailsSortedOnTime.length - 1].dienstregelpuntNaar} -  ${formatTime(new Date(detailsSortedOnTime[detailsSortedOnTime.length - 1].aankomstTijd))}`}
            </div>
          </div>
        </div>
        {inzetPosities && (
          <>
            <div style={{ display: inzetPosities.length > 1 ? 'block' : 'none' }}>
              <RadioField
                name='inzetPositie'
                defaultValue={inzetPosities[0]?.value}
                label='Inzetpositie eerste inzet'
                required
                control={control}
                options={inzetPosities}
                rules={{
                  validate: {
                    inzetPositie: (value) => !!value || 'Dit veld is verplicht',
                  },
                }}
              />
            </div>
            <div style={{ display: inzetPosities.length === 1 ? 'block' : 'none' }}>
              <Label>Inzetpositie eerste inzet</Label>
              <div style={{ color: 'var(--tractie-color-content-default)', paddingLeft: '8px' }}>
                {inzetPosities[0]?.label}
              </div>
            </div>
            <div style={{ marginTop: '-12px', fontSize: '14px', color: 'var(--tractie-color-content-subtle)' }}>
              Opvolgende inzetposities worden automatisch bepaald
            </div>
          </>
        )}

        <MwdDialog.Footer
          submitCaption={`Versterk ${detailsSortedOnTime.length} inzetten`}
          cancelCallback={() => resetDialog()}
          disabled={hasErrors}
        />
      </form>
    </MwdDialog>
  );
};

export default MultiVersterkenDialog;
